/*
 * Fork Instinct brand behaviour (Phase 1). Plain deferred script, ~1KB, no libraries.
 * 1. Fork dividers marked [data-fi-drawable] draw themselves once when scrolled into view.
 *    Without this script they simply show fully drawn.
 * 2. The hero video pauses for visitors who prefer reduced motion.
 * 3. Product recommendations set to "complementary" fall back to Shopify's related picks
 *    when a product has no complementary products, so "Complete the look" never sits empty.
 * 4. The cart drawer "pairs well" card (snippets/fi-cart-cross-sell.liquid) adds to cart in
 *    place and refreshes the drawer, so the next pick appears. Without JS it posts to /cart/add.
 */
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initDividers(root) {
    const svgs = root.querySelectorAll('[data-fi-drawable]:not([data-fi-draw])');
    if (!svgs.length || reduceMotion.matches || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-drawn');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.2 }
    );

    for (const svg of svgs) {
      svg.setAttribute('data-fi-draw', '');
      observer.observe(svg);
    }
  }

  function initHeroVideos(root) {
    if (!reduceMotion.matches) return;
    for (const video of root.querySelectorAll('[data-fi-hero-video]')) {
      video.removeAttribute('autoplay');
      video.pause();
    }
  }

  function initRecsFallback(root) {
    if (!('MutationObserver' in window)) return;
    for (const el of root.querySelectorAll('product-recommendations[data-intent="complementary"]')) {
      if (el.fiRecsWatch) continue;
      const observer = new MutationObserver(() => {
        if (el.dataset.intent !== 'complementary') return;
        if (!el.querySelector('[data-has-recommendations="false"]')) return;
        observer.disconnect();
        el.classList.remove('hidden');
        el.dataset.recommendationsPerformed = 'false';
        el.dataset.intent = 'related';
      });
      el.fiRecsWatch = observer;
      observer.observe(el, { childList: true, subtree: true });
    }
  }

  /* 3b. Complementary picks are rendered server-side (sections/product-recommendations.liquid).
   * When a product has fewer picks than the row holds, top it up with Shopify's related picks,
   * skipping anything already shown. Runs again whenever the section re-renders. */
  async function fillRecs(el) {
    const list = el.querySelector('.resource-list[data-has-recommendations="true"]');
    if (!list || !el.dataset.url || !el.dataset.productId || !el.dataset.sectionId) return;
    const max = parseInt(el.dataset.fiMax || '4', 10) || 4;
    const count = () => list.querySelectorAll(':scope > .resource-list__item').length;
    if (!count() || count() >= max || list.fiFilled) return;
    list.fiFilled = true;

    const seen = new Set([el.dataset.productId]);
    for (const card of list.querySelectorAll('product-card[data-product-id]')) seen.add(card.dataset.productId);

    const base = el.dataset.url.replace(/limit=\d+/, `limit=${Math.min(max + 4, 10)}`);
    const url = `${base}&product_id=${el.dataset.productId}&section_id=${el.dataset.sectionId}&intent=related`;
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      for (const item of doc.querySelectorAll('.resource-list[data-has-recommendations] > .resource-list__item')) {
        if (count() >= max) break;
        const card = item.querySelector('product-card[data-product-id]');
        if (!card || seen.has(card.dataset.productId)) continue;
        seen.add(card.dataset.productId);
        list.appendChild(document.importNode(item, true));
      }
    } catch (e) {
      /* Leave the complementary picks as they are. */
    }
  }

  function initRecsFill(root) {
    for (const el of root.querySelectorAll('product-recommendations')) {
      if (el.fiFillWatch) continue;
      let queued = false;
      const run = () => {
        if (queued) return;
        queued = true;
        setTimeout(() => {
          queued = false;
          fillRecs(el);
        }, 50);
      };
      el.fiFillWatch = new MutationObserver(run);
      el.fiFillWatch.observe(el, { childList: true, subtree: true });
      run();
    }
  }

  /* 3c. The theme only fetches recommendations once an IntersectionObserver fires. Where that never
   * happens (backgrounded or non-rendering tabs, some in-app browsers, crawlers) the row stayed as grey
   * skeletons forever. After the page loads, nudge any section that still has not loaded: any data-*
   * change makes product-recommendations.js load (it ignores this once loaded). */
  function kickRecs(root) {
    if (!window.customElements) return;
    customElements.whenDefined('product-recommendations').then(() => {
      const go = () =>
        setTimeout(() => {
          for (const el of root.querySelectorAll('product-recommendations')) {
            if (el.dataset.recommendationsPerformed === 'true' || el.dataset.error) continue;
            el.dataset.fiKick = String(Date.now());
          }
        }, 1500);
      if (document.readyState === 'complete') go();
      else window.addEventListener('load', go, { once: true });
    });
  }

  function init(root) {
    initDividers(root);
    initHeroVideos(root);
    initRecsFallback(root);
    initRecsFill(root);
    kickRecs(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  /* 4. Cart drawer cross-sell: add without leaving the page, then let the drawer re-render. */
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-fi-xsell')) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button) button.setAttribute('aria-busy', 'true');

    const body = new FormData(form);
    body.delete('return_to');
    const sectionIds = new Set();
    for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
      sectionIds.add(el.dataset.sectionId);
    }
    if (sectionIds.size) body.append('sections', Array.from(sectionIds).join(','));

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (e) {
      events = null;
    }
    const Evt = events && events.CartLinesUpdateEvent;
    const deferred = Evt && typeof Evt.createPromise === 'function' ? Evt.createPromise() : null;
    if (deferred) {
      form.dispatchEvent(
        new Evt({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: body.get('id'), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    const routes = (window.Theme && window.Theme.routes) || {};
    try {
      const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add failed');
      if (!deferred) {
        window.location.href = routes.cart_url || '/cart';
        return;
      }
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((r) => r.json());
      deferred.resolve({
        cart: Evt.createCartFromAjaxResponse ? Evt.createCartFromAjaxResponse(cart) : cart,
        detail: {
          items: cart.items,
          itemCount: 1,
          source: 'fi-cart-cross-sell',
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      if (deferred) deferred.reject(error);
      if (button) button.removeAttribute('aria-busy');
      /* Fall back to a normal form post (lands on the cart page). */
      HTMLFormElement.prototype.submit.call(form);
    }
  });

  /* Re-run for sections added or re-rendered in the theme editor. */
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) init(event.target);
  });
})();
